// Root component.
//
// AuthGate wraps everything: nothing renders until a reviewer signs in with an
// allowlisted email + the shared passphrase. The two work surfaces are
// /supply and /demand — pure operator views. Sign-in lands on /supply; the
// philosophy + SYM context live on /about. No splash, no SYM strip on the
// work pages — every pixel is the meeting.

import { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { AuthGate } from "./components/AuthGate";
import { Sidebar } from "./components/Sidebar";
import { Titlebar } from "./components/Titlebar";
import { DEFAULT_METABASE_SPEC } from "./config/metabase-defaults";
import { listDataSources, saveDataSource } from "./lib/data-sources";
import { AboutPage } from "./pages/AboutPage";
import { AuditPage } from "./pages/AuditPage";
import { ChairmanReportPage } from "./pages/ChairmanReportPage";
import { DemandPage } from "./pages/DemandPage";
import { IntelligencePage } from "./pages/knowledge/IntelligencePage";
import { MeetingLogPage } from "./pages/MeetingLogPage";
import { OnePagerPage } from "./pages/OnePagerPage";
import { SettingsDataSourcesPage } from "./pages/SettingsDataSourcesPage";
import { SupplyPage } from "./pages/SupplyPage";
import { TeamCapacityPage } from "./pages/TeamCapacityPage";

function Shell() {
  const navigate = useNavigate();

  // First-launch: auto-register the baked Metabase data source so value
  // indicators + the Funnel dashboard go LIVE without any setup step.
  useEffect(() => {
    (async () => {
      try {
        const sources = await listDataSources();
        const hasMetabase = sources.some((s) => s.kind.kind === "metabase");
        if (!hasMetabase) {
          await saveDataSource(DEFAULT_METABASE_SPEC);
        }
      } catch (e) {
        console.warn("[bvndle-cc] Metabase auto-register skipped:", e);
      }
    })();
  }, []);

  return (
    <div className="app">
      <Titlebar onBrandClick={() => navigate("/supply")} />
      <div className="body">
        <Sidebar />
        <main className="main">
          <Routes>
            <Route path="/" element={<Navigate to="/supply" replace />} />
            <Route path="/supply" element={<SupplyPage />} />
            <Route path="/demand" element={<DemandPage />} />
            <Route path="/meeting-log" element={<MeetingLogPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/team-capacity" element={<TeamCapacityPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/report/chairman" element={<ChairmanReportPage />} />
            <Route path="/report/one-pager" element={<OnePagerPage />} />
            {/* Knowledge is now a single tabbed surface; old /funnel and
                /ask URLs redirect into the new layout so any bookmarks or
                links the team has saved keep working. */}
            <Route path="/knowledge" element={<IntelligencePage />} />
            <Route path="/funnel" element={<Navigate to="/knowledge?tab=live" replace />} />
            <Route path="/ask" element={<Navigate to="/knowledge?tab=ask" replace />} />
            <Route path="/settings/data-sources" element={<SettingsDataSourcesPage />} />
            <Route path="*" element={<Navigate to="/supply" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export function App() {
  return (
    <AuthGate>
      <Shell />
    </AuthGate>
  );
}
